import styled from '@emotion/styled';
import {
  type EntityEvent,
  EntityEventState,
  type EntityEventStateWithoutNew,
  EntityEventType,
  type EntityEventTypeBasicAlert,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { countChangedKeys } from '@racemap/utilities/functions/countChangedKeys';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import { isDefined } from '@racemap/utilities/functions/validation';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Form,
  type MenuProps,
  Spin,
  Table,
  type TableProps,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DateTime } from 'luxon';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useCurrentUser, useElementDimensions, useEvent } from 'src/lib/customHooks';
import { useEventStarters } from 'src/lib/customHooks/useEventStarters';
import { useAlerts, useMonitorActions } from 'src/store/monitor_beta/monitorStore';
import { IconFilter, IconGear } from '../Icon';
import { AlertEntry } from './AlertEntry';
import { AlertsFilter, type AlertsFilterValues } from './AlertsFilter';
import { getAlertsByAssignment } from './utils/getAlertsByAssignment';
import { getFilteredAlerts } from './utils/getFilteredAlerts';
import { isUserAllowedToUpdateAlert } from './utils/isUserAllowedToUpdateAlert';

type Params = {
  eventSlug: string;
};

export const basicAlertEventTypes: Array<EntityEventTypeBasicAlert> = [
  EntityEventType.LOW_BATTERY,
  EntityEventType.CRASH,
  EntityEventType.NO_MOVEMENT,
  EntityEventType.SOS,
  EntityEventType.POWER_OFF,
  EntityEventType.AWAY_FROM_TRACK,
  EntityEventType.MOVEMENT,
];

export const Alerts: FC = () => {
  const { eventSlug } = useParams<Params>();
  const { event, isLoading } = useEvent({ slug: eventSlug });
  const currentUser = useCurrentUser();
  const { starters: fetchedStarters } = useEventStarters({ eventId: eventSlug });
  const starters = fetchedStarters ?? [];
  const { updateAlertState } = useMonitorActions();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width, height } = useElementDimensions(containerRef);

  const [startTime, endTime] = currentEventTimes(event);
  const startAlertFrame =
    startTime !== undefined ? DateTime.fromMillis(startTime).minus({ hours: 8 }) : DateTime.now();
  const endAlertFrame =
    endTime !== undefined ? DateTime.fromMillis(endTime).plus({ hours: 8 }) : DateTime.now();

  const defaultFilters: AlertsFilterValues = {
    alertEventType: basicAlertEventTypes,
    dateRange: [startAlertFrame, endAlertFrame],
    starterIds: [],
    assignedTo: 'all',
    states: [EntityEventState.NEW, EntityEventState.IN_PROGRESS, EntityEventState.STANDBY],
  };
  const [form] = Form.useForm<AlertsFilterValues>();
  const [filters, setFilters] = useState<AlertsFilterValues>(defaultFilters);

  const handleReset = () => {
    form.resetFields();
    handleFiltersChange(defaultFilters);
  };

  const filterCount = countChangedKeys(defaultFilters, filters);

  useEffect(() => {
    const [newStartTime, newEndTime] = currentEventTimes(event);
    const newStartAlertFrame =
      newStartTime !== undefined
        ? DateTime.fromMillis(newStartTime).minus({ hours: 8 })
        : DateTime.now();
    const newEndAlertFrame =
      newEndTime !== undefined
        ? DateTime.fromMillis(newEndTime).plus({ hours: 8 })
        : DateTime.now();
    handleFiltersChange({
      dateRange: [newStartAlertFrame, newEndAlertFrame],
    });
  }, [event]);

  const handleFiltersChange = (newFilters: AlertsFilterValues) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const alerts = useAlerts();

  const count = countChangedKeys(defaultFilters, filters);

  const starterDeviceIds =
    starters
      .filter((s) => filters.starterIds?.includes(s.id.toHexString()) ?? false)
      .map((s) => s.deviceId?.toHexString())
      .filter(isDefined) ?? [];

  const { alertEventType, dateRange, assignedTo, states, starterIds } = filters;
  const filteredAlerts = useMemo(() => {
    const filtered = getFilteredAlerts(
      alerts,
      alertEventType,
      dateRange,
      starterDeviceIds,
      states,
      starterIds,
    );
    return getAlertsByAssignment(filtered, currentUser, assignedTo).sort(
      (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
    );
  }, [alerts, filters, currentUser]);

  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Array<string>>([]);

  const setStateAndAssign = async (alertIds: Array<string>, state: EntityEventStateWithoutNew) => {
    try {
      await updateAlertState(alertIds, state);
      toast.success(
        `${alertIds.length} alert${alertIds.length !== 1 ? 's' : ''} marked as "${state
          .replace('_', ' ')
          .toLowerCase()}" and assigned to you.`,
      );
    } catch (error) {
      console.error(
        `Error updating ${alertIds.length} alert${alertIds.length !== 1 ? 's' : ''} to '${state}':`,
        error,
      );
      toast.error(`Could not update ${alertIds.length} alert${alertIds.length !== 1 ? 's' : ''}.`);
    }
  };

  const handleBulk = useCallback(
    (state: EntityEventStateWithoutNew) => {
      setStateAndAssign(selectedAlertIds, state);
      setSelectedAlertIds([]);
    },
    [selectedAlertIds, setStateAndAssign],
  );

  const menuItems = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'info',
        label: (
          <AlertsInfo>
            <b>{selectedAlertIds.length}</b> {selectedAlertIds.length === 1 ? 'alert' : 'alerts'} in
            selection
          </AlertsInfo>
        ),
        disabled: true,
      },
      { type: 'divider' as const },
      { key: 'resolve', onClick: () => handleBulk(EntityEventState.RESOLVED), label: <>Resolve</> },
      {
        key: 'assign',
        onClick: () => handleBulk(EntityEventState.IN_PROGRESS),
        label: <>Assign</>,
      },
      { key: 'standby', onClick: () => handleBulk(EntityEventState.STANDBY), label: <>Standby</> },
    ],
    [handleBulk, selectedAlertIds.length],
  );

  const columns: ColumnsType<EntityEvent> = useMemo(() => {
    if (!event) {
      return [];
    }

    return [
      {
        title: '',
        dataIndex: 'alert',
        key: 'alert',
        render: (_: any, record: EntityEvent) => (
          <AlertEntry
            alert={record}
            currentUser={currentUser}
            event={event}
            setStateAndAssign={setStateAndAssign}
          />
        ),
      },
    ];
  }, [currentUser, event]);

  if (isLoading || !event)
    return (
      <Container>
        <Spin />
      </Container>
    );

  const canUpdateAlert = (record: EntityEvent) =>
    isUserAllowedToUpdateAlert(record, currentUser, event);

  const rowSelection = {
    columnWidth: 50,
    selectedRowKeys: selectedAlertIds,
    onChange: (_selectedRowKeys: Array<React.Key>, selectedRows: Array<EntityEvent>) => {
      setSelectedAlertIds(selectedRows.map((row) => row.id.toHexString()));
    },
    getCheckboxProps: (alert: EntityEvent) => ({
      disabled: !canUpdateAlert(alert),
    }),
    renderCell: (
      _checked: boolean,
      record: EntityEvent,
      _index: number,
      originNode: React.ReactNode,
    ) => {
      if (!canUpdateAlert(record)) {
        return null;
      }
      return originNode;
    },
  };

  return (
    <Flex vertical style={{ height: '100%', width: '100%' }}>
      <SortFilterHeader>
        <Dropdown
          menu={{
            items: menuItems,
          }}
          disabled={selectedAlertIds.length === 0}
          trigger={['click']}
        >
          <Badge count={selectedAlertIds.length} size={'small'} color={RacemapColors.PaleBlue}>
            <SettingsButton disabled={selectedAlertIds.length === 0} icon={<IconGear />} />
          </Badge>
        </Dropdown>
        <RightActions>
          <Button danger onClick={handleReset} disabled={filterCount === 0}>
            Reset
          </Button>
          <FilterBadge count={count} size="small" color={RacemapColors.PaleBlue} offset={[-2, 4]}>
            <Button icon={<IconFilter />} onClick={() => setFilterVisible((prev) => !prev)} />
          </FilterBadge>
        </RightActions>
      </SortFilterHeader>
      <FilterContainer isVisible={filterVisible}>
        <AlertsFilter
          form={form}
          starters={starters}
          defaultFilters={defaultFilters}
          minDate={startAlertFrame}
          maxDate={endAlertFrame}
          onFiltersChange={handleFiltersChange}
        />
      </FilterContainer>
      <Wrapper ref={containerRef}>
        {filteredAlerts.length === 0 ? (
          <EmptyState>No alerts found.</EmptyState>
        ) : (
          <CenteredCheckboxTable
            columns={columns}
            dataSource={filteredAlerts}
            pagination={false}
            rowKey={(record) => record.id.toHexString()}
            scroll={{ x: width, y: height - 43 }}
            rowSelection={rowSelection}
            size="small"
            virtual
          />
        )}
      </Wrapper>
    </Flex>
  );
};

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${RacemapColors.DarkGray};
`;

const SortFilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: ${RacemapColors.LightGray};
  align-items: center;
`;

const FilterBadge = styled(Badge)` 
`;

const SettingsButton = styled(Button)`
`;

const FilterContainer = styled.div<{ isVisible: boolean }>`
  background: ${RacemapColors.LightLightGray};
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  padding: ${({ isVisible }) => (isVisible ? '24px 12px 12px 12px' : '0')};
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;

const AlertsInfo = styled.div`
  color: black;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const CenteredCheckboxTable = styled(Table as unknown as React.ComponentType)<
  TableProps<EntityEvent>
>`
  .ant-table-selection-column .ant-checkbox-wrapper {
    position: relative;
    top: 40%;
  }
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
